import { decodeControlMessage, type ControlMessage, type PhotoSource } from '@fastgarage/protocol';

/**
 * Dispatcher for control messages received over the LiveKit data channel.
 * Pure logic: every side effect (capture, torch, UI overlays, upload) is
 * injected via `CommandDeps`, so the fallback paths are unit-testable without
 * browser APIs.
 */

export interface CapturedPhoto {
  blob: Blob;
  source: PhotoSource;
}

export interface CommandDeps {
  /** Currently published local camera track, or null when none is active. */
  getVideoTrack(): MediaStreamTrack | null;
  /** Whether ImageCapture.takePhoto() is available on this browser. */
  hasImageCapture(): boolean;
  captureViaImageCapture(track: MediaStreamTrack): Promise<Blob>;
  captureViaCanvas(track: MediaStreamTrack): Promise<Blob>;
  setTorch(track: MediaStreamTrack, on: boolean): Promise<void>;
  setZoom(track: MediaStreamTrack, zoom: number): Promise<void>;
  /** Switch the published camera: exact `deviceId` when the staff picked one
   * (external USB cameras have no facing mode), otherwise by facing mode. */
  switchCamera(target: { deviceId?: string; facing?: 'user' | 'environment' }): Promise<void>;
  showPointer(x: number, y: number): void;
  showHint(text: string): void;
  /** Hash + measure + enqueue for upload; rejects on unrecoverable errors. */
  submitPhoto(requestId: string, photo: CapturedPhoto): Promise<void>;
  /** Sends PHOTO_ERROR back over the data channel. */
  reportError(requestId: string, reason: string): void;
}

export interface CommandDispatcher {
  /** Handles an already-decoded control message. */
  handle(message: ControlMessage): Promise<void>;
  /** Decodes raw data-channel bytes; malformed payloads are silently dropped. */
  handleData(payload: Uint8Array): void;
}

export function newRequestId(): string {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `req-${Date.now()}-${Math.floor(Math.random() * 1_000_000)}`;
}

export function createCommandDispatcher(deps: CommandDeps): CommandDispatcher {
  async function handleTakePhoto(requestId: string): Promise<void> {
    const track = deps.getVideoTrack();
    if (!track) {
      deps.reportError(requestId, 'Keine aktive Kamera');
      return;
    }
    let photo: CapturedPhoto | null = null;
    if (deps.hasImageCapture()) {
      try {
        photo = { blob: await deps.captureViaImageCapture(track), source: 'image_capture' };
      } catch {
        // ImageCapture failed at runtime — fall back to the canvas path.
        photo = null;
      }
    }
    if (!photo) {
      try {
        photo = { blob: await deps.captureViaCanvas(track), source: 'canvas' };
      } catch {
        deps.reportError(requestId, 'Foto konnte nicht aufgenommen werden');
        return;
      }
    }
    try {
      await deps.submitPhoto(requestId, photo);
    } catch {
      deps.reportError(requestId, 'Foto konnte nicht verarbeitet werden');
    }
  }

  async function handle(message: ControlMessage): Promise<void> {
    switch (message.type) {
      case 'TAKE_PHOTO':
        await handleTakePhoto(message.requestId);
        break;
      case 'SET_TORCH': {
        const track = deps.getVideoTrack();
        if (track) {
          // No reply required; failures are ignored gracefully.
          await deps.setTorch(track, message.on).catch(() => undefined);
        }
        break;
      }
      case 'SET_ZOOM': {
        const track = deps.getVideoTrack();
        if (track) {
          await deps.setZoom(track, message.zoom).catch(() => undefined);
        }
        break;
      }
      case 'SWITCH_CAMERA':
        await deps
          .switchCamera({ deviceId: message.deviceId, facing: message.facing })
          .catch(() => undefined);
        break;
      case 'POINTER':
        deps.showPointer(message.x, message.y);
        break;
      case 'HINT':
        deps.showHint(message.text);
        break;
      case 'PHOTO_DONE':
      case 'PHOTO_ERROR':
      case 'CAPABILITIES':
        // Outbound-only message types — ignore if a peer echoes them.
        break;
    }
  }

  function handleData(payload: Uint8Array): void {
    const message = decodeControlMessage(payload);
    if (!message) return;
    void handle(message);
  }

  return { handle, handleData };
}
